import {
  addElement,
  defaultGenericElement,
  reversionElement,
  updateElement,
} from '../../lib/element';
import { prepareElementForExport } from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { useEditorStore } from '../../stores/useEditorStore';
import { type ImageElement } from '../image';
import { poly, type GenericPolyElement } from '../poly';

import {
  calculateColorCheckerShape,
  getColorCheckerBezierHandles,
  getColorCheckerPath2D,
  getColorCheckerPoints,
  initializeColorCheckerGeometry,
} from './utils';

export type ColorCheckerMode =
  | 'circle'
  | 'rectangle'
  | 'circle-donut'
  | 'rectangle-donut';

export interface ColorCheckerElement extends GenericPolyElement {
  type: 'colorchecker';
  mode: ColorCheckerMode;
}

export type ColorCheckerPlugin = Plugin<
  ColorCheckerElement,
  typeof colorCheckerActions,
  typeof colorCheckerState
>;

const colorCheckerState = {};

const DEFAULT_SIZE = 200;
const COLOR_CHECKER_LAYER = 100_000;

const colorCheckerActions = {
  toggle: (
    id: string,
    options?: { visibility?: boolean; color?: string },
  ): ColorCheckerElement => {
    const elements = useEditorStore.getState().elements;
    const existingColorChecker = elements.get(id) as
      | ColorCheckerElement
      | undefined;

    if (existingColorChecker) {
      const updatedColorChecker = {
        ...existingColorChecker,
        isVisible: options?.visibility ?? !existingColorChecker.isVisible,
        ...(options?.color && { strokeColor: options.color }),
      };

      updateElement(elements, existingColorChecker, updatedColorChecker, true);

      return updatedColorChecker;
    }

    const imageElement = Array.from(elements.values()).find(
      (e): e is ImageElement => e.type === 'image',
    );

    const newElement: ColorCheckerElement = {
      ...defaultGenericElement(),
      id,
      mode: 'rectangle',
      type: 'colorchecker',
      layer: COLOR_CHECKER_LAYER,
      isVisible: true,
      points: [
        [0, 0],
        [0, 0],
        [0, 0],
        [0, 0],
      ],
      path: new Path2D(),
      strokeColor: options?.color ?? '#000000',
      strokeWidth: 1,
      width: DEFAULT_SIZE,
      height: DEFAULT_SIZE,
      originX: 0,
      originY: 0,
      x: 0,
      y: 0,
      ...(imageElement && {
        x: imageElement.x + imageElement.width / 2 - DEFAULT_SIZE / 2,
        y: imageElement.y + imageElement.height / 2 - DEFAULT_SIZE / 2,
        originX: imageElement.x + imageElement.width / 2 - DEFAULT_SIZE / 2,
        originY: imageElement.y + imageElement.height / 2 - DEFAULT_SIZE / 2,
      }),
    };

    initializeColorCheckerGeometry(newElement);

    addElement(useEditorStore.getState().elements, newElement);

    renderStaticScene();

    return newElement;
  },
  update: (
    id: string,
    updates: Partial<ColorCheckerElement>,
  ): ColorCheckerElement | null => {
    const elements = useEditorStore.getState().elements;
    const colorCheckerElement = elements.get(id) as
      | ColorCheckerElement
      | undefined;

    if (!colorCheckerElement) return null;

    const mergedUpdates: Partial<ColorCheckerElement> = { ...updates };
    const newMode = updates.mode ?? colorCheckerElement.mode;
    const newWidth = updates.width ?? colorCheckerElement.width;
    const newHeight = updates.height ?? colorCheckerElement.height;

    const geometryChanged =
      updates.mode !== undefined ||
      updates.width !== undefined ||
      updates.height !== undefined;
    const positionChanged = updates.x !== undefined || updates.y !== undefined;

    if (geometryChanged || positionChanged) {
      const tempElement = {
        ...colorCheckerElement,
        ...mergedUpdates,
      };

      if (geometryChanged) {
        tempElement.points = getColorCheckerPoints(
          newMode,
          newWidth,
          newHeight,
        );
        tempElement.bezierHandles = getColorCheckerBezierHandles(
          newMode,
          newWidth,
          newHeight,
        );
        mergedUpdates.points = tempElement.points;
        mergedUpdates.bezierHandles = tempElement.bezierHandles;
      }

      mergedUpdates.shape = calculateColorCheckerShape(tempElement);
      mergedUpdates.version = {
        ...colorCheckerElement.version,
        ...reversionElement(colorCheckerElement, ['bounds', 'canvas']).version,
      };
    }

    updateElement(elements, colorCheckerElement, mergedUpdates, true);

    return { ...colorCheckerElement, ...mergedUpdates };
  },
};

const plugin: ColorCheckerPlugin = {
  name: 'colorchecker',
  cursor: 'default',
  actions: colorCheckerActions,
  state: colorCheckerState,
  events: {},
  render: {
    padding: (element) => element.strokeWidth,
    draw: ({ config, context, element }) => {
      context.save();

      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = element.strokeWidth / 2;
      context.fillStyle = element.strokeColor;

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      const path = getColorCheckerPath2D(element);
      context.fill(path, 'evenodd');

      context.restore();
    },
    translate: () => undefined,
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: true,
    bounds: ({ x, y, width, height }) => [
      x,
      y,
      x + width,
      y + height,
      x + width / 2,
      y + height / 2,
    ],
    calculateShape: calculateColorCheckerShape,
    isPointInShape: poly.collision.isPointInShape,
    isPointOnShape: () => false,
  },
  selection: {
    renderHoverBorder: (ctx, element, { config, state }) => {
      if (!element.shape) return;

      ctx.save();
      ctx.translate(state.scrollX, state.scrollY);

      ctx.lineWidth = config.hover.lineWidth / state.zoom.value;
      ctx.strokeStyle = config.hover.strokeStyle;

      ctx.stroke(new Path2D(element.shape.data));

      ctx.restore();
    },
    renderSelectionBorder: () => undefined,
    hideDefaultTransformHandles: true,
  },
  transformation: {
    shiftBySizeOnFlip: poly.transformation.shiftBySizeOnFlip,
    resizedBounds: poly.transformation.resizedBounds,
    transformElement: poly.transformation.transformElement,
  },
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<ColorCheckerElement>;

    delete prepared.points;
    delete prepared.bezierHandles;
    delete prepared.path;

    return prepared;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
      path: new Path2D(),
      layer: COLOR_CHECKER_LAYER,
      isVisible: false,
    } as ColorCheckerElement;

    initializeColorCheckerGeometry(imported);

    return imported;
  },
};

export const colorChecker = plugin;
